import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["onChange", "type"];
import React, { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createUseStyles, Input } from 'hoi-poi-ui';
import styles from "./styles";
const useStyles = createUseStyles(styles, 'InputCell');
const decimalFilter = text => {
  let textArr = [];
  let textValue = text.replace(/[^-,.0-9\s]/g, '');
  textValue.split('').forEach((current, index) => {
    if (index === 0 && current !== '.' && current !== ',') {
      textArr.push(current);
    } else if (index > 0 && (current === '.' || current === ',') && !textArr.includes('.') && !textArr.includes(',') && !isNaN(parseInt(textArr[index - 1], 10))) {
      textArr.push(current);
    } else if (current !== '-' && current !== '.' && current !== ',') {
      textArr.push(current);
    }
  });
  textValue = textArr.join('');
  textValue = textValue.replace(',', '.');
  return textValue;
};
const filters = {
  natural: text => {
    return ('' + text).replace(/[^0-9\s]/g, '');
  },
  integer: text => {
    return ('' + text).replace(/[^-0-9\s]/g, '');
  },
  number: text => {
    return ('' + text).replace(/[^-0-9\s]/g, '');
  },
  decimal: decimalFilter,
  currency: decimalFilter,
  percentage: decimalFilter
};
const blurInteger = text => {
  return parseInt(text, 10) || 0;
};
const blurDecimal = text => {
  return parseFloat(text) || 0.0;
};
const blurPercentage = text => {
  let value = parseFloat(text) || 0.0;
  return value > 100 ? 100 : value;
};
const blurFilters = {
  natural: blurInteger,
  integer: blurInteger,
  number: blurInteger,
  decimal: blurDecimal,
  currency: blurDecimal,
  percentage: blurPercentage
};
const InputCell = /*#__PURE__*/memo(params => {
  var _params$otherFields;
  const inputRef = useRef();
  const [value, setValue] = useState(params.value || '');
  const [error, setError] = useState('');
  const classes = useStyles();
  const valueRef = useRef(params.value || '');
  const field = params.colDef.field;
  const _params$inputParams = params.inputParams,
    {
      onChange,
      type
    } = _params$inputParams,
    inputParams = _objectWithoutProperties(_params$inputParams, _excluded);
  useEffect(() => {
    if (params.value !== valueRef.current) {
      setError('');
      valueRef.current = params.value;
      setValue(params.value);
    }
  }, [params.value]);
  useEffect(() => {
    var _inputParams$errors, _inputParams$errors$r, _inputParams$errors$r2;
    const rowIndex = params.node.rowIndex;
    if (!(inputParams !== null && inputParams !== void 0 && (_inputParams$errors = inputParams.errors) !== null && _inputParams$errors !== void 0 && (_inputParams$errors$r = _inputParams$errors.rows) !== null && _inputParams$errors$r !== void 0 && (_inputParams$errors$r2 = _inputParams$errors$r[rowIndex]) !== null && _inputParams$errors$r2 !== void 0 && _inputParams$errors$r2.error)) {
      setError('');
    } else {
      var _inputParams$errors2, _inputParams$errors2$, _inputParams$errors2$2;
      const errorText = inputParams === null || inputParams === void 0 ? void 0 : (_inputParams$errors2 = inputParams.errors) === null || _inputParams$errors2 === void 0 ? void 0 : (_inputParams$errors2$ = _inputParams$errors2.rows) === null || _inputParams$errors2$ === void 0 ? void 0 : (_inputParams$errors2$2 = _inputParams$errors2$[rowIndex]) === null || _inputParams$errors2$2 === void 0 ? void 0 : _inputParams$errors2$2.error;
      setError(errorText);
    }
  }, [inputParams.errors, params]);
  const handleOnChange = useCallback(val => {
    let newValue = val || '';
    const filter = filters[type] || null;
    if (filter) {
      newValue = filter(newValue);
    }
    setError('');
    valueRef.current = newValue;
    setValue(newValue);
  }, [type]);
  const handleOnBlur = useCallback(() => {
    const id = params.data.id || params.data.Id || params.data.ID || '';
    const blurFilter = blurFilters[type] || null;
    let newValue = valueRef.current;
    if (blurFilter) {
      newValue = blurFilter(valueRef.current);
    }
    onChange({
      rowIndex: params.node.rowIndex,
      id,
      field,
      value: newValue
    });
  }, [params, onChange, field, type]);
  const handleOnEnter = useCallback(() => {
    var _inputRef$current;
    handleOnBlur();
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.blur();
  }, [handleOnBlur]);
  const overrides = useMemo(() => ({
    input: {
      ref: inputRef
    }
  }), []);
  if (!inputParams) return null;
  const defaultPreComponent = currencySymbol => {
    return /*#__PURE__*/React.createElement("div", null, currencySymbol);
  };
  const defaultPostComponent = currencySymbol => {
    return /*#__PURE__*/React.createElement("div", null, currencySymbol);
  };
  let preComponent = (inputParams === null || inputParams === void 0 ? void 0 : inputParams.preComponent) || null;
  let postComponent = (inputParams === null || inputParams === void 0 ? void 0 : inputParams.postComponent) || null;
  if (params.colDef.crudType === 'currency' && params !== null && params !== void 0 && (_params$otherFields = params.otherFields) !== null && _params$otherFields !== void 0 && _params$otherFields.symbol) {
    var _params$otherFields2;
    const symbol = params.data[params === null || params === void 0 ? void 0 : (_params$otherFields2 = params.otherFields) === null || _params$otherFields2 === void 0 ? void 0 : _params$otherFields2.symbol];
    if (symbol && params.currencyPosition) {
      if (params.currencyPosition === 'before') {
        preComponent = defaultPreComponent(symbol);
        postComponent = null;
      } else if (params.currencyPosition === 'after') {
        preComponent = null;
        postComponent = defaultPostComponent(symbol);
      }
    }
  }
  let classNames = ['w5-cell', classes.editableCell];
  if (params.align === 'right') classNames.push(classes.right);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames.join(' ')
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Input, _extends({
    value: value,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onEnter: handleOnEnter
  }, inputParams, {
    preComponent: preComponent,
    postComponent: postComponent,
    error: error,
    overrides: overrides,
    isFullWidth: true
  }))));
});
export default InputCell;